// The sandbox backends and what each one does with a size, a mount and a
// warm sandbox. The only place a backend name is interpreted; no CHECK
// closes the list on any table. Pure, so the browser bundle imports it too.
// See ADR-0043.

/** The backend names: Docker on the factory's host, Daytona on the vendor's cloud. */
export const SANDBOX_BACKENDS = ["docker", "daytona"] as const;

/** Where a sandbox runs and what creates it. */
export type SandboxBackend = (typeof SANDBOX_BACKENDS)[number];

/** Whether a string is one of the backend names. */
export function isSandboxBackend(value: string): value is SandboxBackend {
  return (SANDBOX_BACKENDS as readonly string[]).includes(value);
}

/**
 * The platform's backend when the operator has never set one on the
 * Platform screen. A Run dispatches on the Organisation's override else the
 * platform value, resolved live in platform-sandbox.ts; nothing else reads
 * this constant as "the backend".
 */
export const DEFAULT_SANDBOX_BACKEND: SandboxBackend = "docker";

/** The three dimensions of a size row. */
export const SIZE_DIMENSIONS = ["cpu", "memory", "disk"] as const;

/** One dimension of a size row. */
export type SizeDimension = (typeof SIZE_DIMENSIONS)[number];

/**
 * What a backend does with one dimension: caps the sandbox at it, asks for
 * it without capping, or cannot express it.
 */
export type Enforcement = "enforced" | "requested" | "ignored";

/** A backend's enforcement of each dimension. */
export type SizeEnforcement = Record<SizeDimension, Enforcement>;

/**
 * The enforcement table. Docker maps cores to `--cpus`, has no memory cap
 * yet and shares the host's disk; a Daytona sandbox is created from a
 * snapshot that carries its allocation, so all three bind. The Environment
 * page labels each dimension off this table.
 */
export const ENFORCEMENT_BY_BACKEND: Record<SandboxBackend, SizeEnforcement> = {
  docker: { cpu: "enforced", memory: "requested", disk: "ignored" },
  daytona: { cpu: "enforced", memory: "enforced", disk: "enforced" },
};

/** The page's word for each dimension. */
export const DIMENSION_LABELS: Record<SizeDimension, string> = {
  cpu: "CPU",
  memory: "Memory",
  disk: "Disk",
};

/** The page's hint for each enforcement. */
export const ENFORCEMENT_HINTS: Record<Enforcement, string> = {
  enforced: "the sandbox is capped at the size's figure",
  requested: "carried with the size, not yet capped on the current backend",
  ignored: "not applied; the sandbox shares the host's",
};

/**
 * How a backend's sandbox reaches the host's directories. A bind-mount
 * provider mounts them in place; an isolated provider shares nothing, so
 * the executor carries the same directories in and out over `exec`.
 */
export type SandboxShape = "bind-mount" | "isolated";

/** The shape of each backend. The executor asks once, where it opens the sandbox. */
export const SHAPE_BY_BACKEND: Record<SandboxBackend, SandboxShape> = {
  docker: "bind-mount",
  daytona: "isolated",
};

/**
 * Whether a Task's sandbox may stay warm between its implementation Run and
 * its review Run. Docker's may: the container idles on the factory's host
 * at no cost. Daytona's may not: it is deleted when the Run ends, whatever
 * the outcome, because a live sandbox at the vendor is metered until deleted.
 * See ADR-0028 §5 and ADR-0043 §6.
 */
export const WARM_BETWEEN_RUNS_BY_BACKEND: Record<SandboxBackend, boolean> = {
  docker: true,
  daytona: false,
};
